import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { SendHorizontal, RefreshCw } from "lucide-react";
import {
  addMessage,
  initializeChatWithThread,
  sendFirstMessage,
  markChatAsRead,
  setPersonaLoading,
} from "@/store/slices/chatSlice";
import type { RootState } from "@/store";
import type { Message, ChatHistory } from "@/types/chat";
import { threadService } from "@/services/threads";
import ReactMarkdown from "react-markdown";
import { useAppDispatch } from "@/hooks/useAppDispatch";

interface ChatProps {
  personaId: string;
  personaName: string;
}

export function Chat({ personaId, personaName }: ChatProps) {
  const dispatch = useAppDispatch();
  const [newMessage, setNewMessage] = useState("");
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Get messages from Redux store
  const currentChat = useSelector((state: RootState) =>
    state.chat.chats.find(
      (chat: ChatHistory) => chat.personaName === personaName
    )
  );
  const messages = currentChat?.messages || [];
  const unreadCount = currentChat?.unreadCount || 0;
  const threadId = currentChat?.threadId;
  const isBotLoading = useSelector(
    (state: RootState) => state.chat.loadingByPersona[personaName]
  );

  // Initialize chat when a persona is selected
  useEffect(() => {
    if (personaId && !currentChat) {
      dispatch(
        initializeChatWithThread({
          personaId,
          personaName,
        })
      );
    }
  }, [personaId, personaName, dispatch, currentChat]);

  // Mark chat as read when viewed
  useEffect(() => {
    if (personaName && unreadCount > 0) {
      dispatch(markChatAsRead(personaName));
    }
  }, [personaName, unreadCount, dispatch]);

  // Scroll automatico in fondo ai messaggi
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (newMessage.trim() && personaName) {
      try {
        dispatch(setPersonaLoading({ personaName, loading: true }));
        const currentMessage = newMessage;
        setNewMessage("");

        if (threadId) {
          // Thread exists, send normal message
          const message: Message = {
            id: `user-${Date.now()}`,
            text: currentMessage,
            sender: "user",
            timestamp: new Date().toISOString(),
          };
          dispatch(addMessage({ personaName, message }));

          const response = await threadService.sendMessage(
            currentMessage,
            threadId
          );
          const botResponse: Message = {
            id: `bot-${Date.now()}`,
            text: response.assistant,
            sender: "bot",
            timestamp: new Date().toISOString(),
          };
          dispatch(addMessage({ personaName, message: botResponse }));
        } else {
          // No thread exists, send first message which will create the thread
          await dispatch(
            sendFirstMessage({
              personaId,
              personaName,
              userPrompt: currentMessage,
            })
          ).unwrap();
        }

        dispatch(setPersonaLoading({ personaName, loading: false }));
      } catch (error) {
        console.error("Error while sending the message:", error);
        dispatch(setPersonaLoading({ personaName, loading: false }));
      }
    }
  };

  const handleResetThread = async () => {
    if (personaName) {
      try {
        dispatch(setPersonaLoading({ personaName, loading: true }));
        await dispatch(
          initializeChatWithThread({
            personaId,
            personaName,
            resetThread: true,
          })
        ).unwrap();
      } catch (error) {
        console.error("Error during thread reset:", error);
      } finally {
        dispatch(setPersonaLoading({ personaName, loading: false }));
      }
    }
  };

  if (!personaName) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-lg">
        Select a persona
      </div>
    );
  }

  return (
    <Card className="w-full h-full flex flex-col bg-white border-gray-200">
      {/* Header */}
      <CardHeader className="border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2 text-gray-800">
            {personaName ? `Chat - ${personaName}` : "Chat - Select a persona"}
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                {unreadCount}
              </span>
            )}
            {threadId && (
              <span className="bg-amber-500 text-white text-xs px-2 py-1 rounded-full">
                Thread: {threadId.slice(0, 8)}...
              </span>
            )}
          </CardTitle>
          {personaName && (
            <Button
              onClick={handleResetThread}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 border-gray-300 text-gray-700 bg-transparent hover:bg-gray-100 hover:text-gray-900 hover:border-gray-400"
              disabled={isBotLoading}
            >
              <RefreshCw className="h-4 w-4" />
              New Thread
            </Button>
          )}
        </div>
      </CardHeader>

      {/* Messages + Loading */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full min-h-[200px]">
                <div className="text-center text-gray-500">
                  <div className="text-6xl mb-4">💬</div>
                  <h3 className="text-lg font-medium text-gray-700 mb-2">
                    Hi! I'm {personaName}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Write a message to start chatting with the AI
                  </p>
                </div>
              </div>
            ) : (
              messages.map((message: Message) => (
                <div
                  key={message.id}
                  className={`flex items-start gap-2 ${
                    message.sender === "user" ? "flex-row-reverse" : "flex-row"
                  }`}
                >
                  <div
                    className={`rounded-lg p-3 max-w-[90%] ${
                      message.sender === "user"
                        ? "bg-amber-500 text-white"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {message.sender === "bot" ? (
                      <ReactMarkdown>{message.text}</ReactMarkdown>
                    ) : (
                      <p className="text-sm">{message.text}</p>
                    )}
                    <span className="text-xs opacity-70 mt-1 block">
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              ))
            )}
            {/* Ref for automatic scroll */}
            <div ref={messagesEndRef} />
          </div>
        </div>
        {isBotLoading && (
          <div className="flex items-center gap-2 text-gray-500 text-sm p-2 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <span className="animate-pulse">🤖 Bot is typing...</span>
          </div>
        )}
      </div>

      {/* Input always at bottom */}
      <div className="border-t border-gray-200 p-4 flex-shrink-0 bg-white">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
            className="flex-1 bg-white border-gray-300 text-gray-800 placeholder-gray-500 focus:border-amber-400 focus:ring-amber-400"
          />
          <Button
            onClick={handleSendMessage}
            size="icon"
            className="shrink-0 bg-amber-500 hover:bg-amber-600 text-white"
          >
            <SendHorizontal className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
